/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-runloop, ember/require-tagless-components */
import {
  attributeBindings,
  classNames,
  tagName,
} from '@ember-decorators/component';
// Ember Imports
import Component from '@ember/component';
import { scheduleOnce } from '@ember/runloop';
// D3
import { select } from 'd3-selection';

const Y_W_WIDE = 72; // Original desktop year-label column width
const Y_W_NARROW = 40; // Narrower column used on mobile to gain data space
const COL_W = 90;
const HEADER_H = 34;
const COL_H_PAD = 12; // vertical padding above + below text in column headers
const ROW_H = 30;
const BADGE_H = 20;
const BADGE_R = 10;
const BADGE_FONT = 10;
const PAIR_BADGE_H = 17;
const PAIR_BADGE_R = 8;
const PAIR_BADGE_FONT = 10;
const PAIR_GAP = 8;

@tagName('svg')
@classNames('chart', 'report-builder-progress-table')
@attributeBindings('preserveAspectRatio', 'viewBox')
export default class MoeProgressTable extends Component {
  preserveAspectRatio = 'xMinYMin meet';
  gradeColumns = null;
  displayRows = null;
  themeColors = null;
  viewBox = null;
  width = null;
  showHeader = true;
  headerName = 'Sample Student';
  headerReport = 'Report 1';
  headerMeta = 'Subject | Years 1 to 6';

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    scheduleOnce('render', this, this.draw);
  }

  draw() {
    let cols = this.gradeColumns || [];
    let rows = this.displayRows || [];
    let themeColors = this.themeColors || {};

    let nCols = cols.length;
    let nRows = rows.length;
    if (!nCols || !nRows) {
      return;
    }

    // When a rendered pixel width is provided (via ChartResizer), fill it exactly
    // so coordinates map 1:1 to pixels and font sizes render at their nominal size.
    let headerH = this.showHeader ? HEADER_H : 0;

    // Determine layout mode using the full (desktop) year-label column width.
    // Wide (desktop) layouts keep the original 72 px column; narrow (mobile)
    // layouts shrink it to 40 px so data columns have more room.
    let wideAvailW = (this.width || Y_W_WIDE + nCols * COL_W) - Y_W_WIDE;
    let isWide = Math.floor(wideAvailW / nCols) >= COL_W;
    // Shadow the module-level constants with the layout-appropriate value so
    // all drawing code below automatically uses the correct column width.
    let Y_W = isWide ? Y_W_WIDE : Y_W_NARROW;

    let availW = isWide
      ? wideAvailW
      : (this.width || Y_W + nCols * COL_W) - Y_W;
    let colWidths;

    if (isWide) {
      // Wide view: equal column widths — original behaviour unchanged.
      let evenColW = Math.floor(availW / nCols);
      colWidths = cols.map(() => evenColW);
      colWidths[colWidths.length - 1] += availW - evenColW * nCols;
    } else {
      // Narrow (mobile) view: proportional widths so short labels like "Ok"
      // don't steal space from longer ones, with a badge-content minimum.
      const CHAR_PX = 7;
      const COL_PAD = 12;
      const MIN_COL_W = 55; // minimum to fit a date badge ("Jun 25")

      // Pass 1 — proportional from label length
      let naturalWidths = cols.map(
        (col) => col.label.length * CHAR_PX + COL_PAD,
      );
      let totalNatural = naturalWidths.reduce((s, w) => s + w, 0);
      let scale = availW / totalNatural;
      colWidths = naturalWidths.map((w) => Math.floor(w * scale));

      // Pass 2 — enforce minimum, borrowing from larger columns
      let deficit = 0;
      colWidths = colWidths.map((w) => {
        if (w < MIN_COL_W) {
          deficit += MIN_COL_W - w;
          return MIN_COL_W;
        }
        return w;
      });
      if (deficit > 0) {
        let aboveExtra = colWidths.reduce(
          (s, w) => s + Math.max(w - MIN_COL_W, 0),
          0,
        );
        if (aboveExtra > 0) {
          colWidths = colWidths.map((w) => {
            if (w <= MIN_COL_W) return w;
            return w - Math.round(((w - MIN_COL_W) / aboveExtra) * deficit);
          });
        }
      }

      // Fix rounding remainder on the widest column
      let allocated = colWidths.reduce((s, w) => s + w, 0);
      let widestIdx = colWidths.indexOf(Math.max(...colWidths));
      colWidths[widestIdx] += availW - allocated;
    }
    let colOffsets = [];
    let _accX = 0;
    colWidths.forEach((w) => {
      colOffsets.push(_accX);
      _accX += w;
    });
    let totalW = Y_W + _accX;

    // Derive COL_H from the tallest wrapped column label so it always fits.
    let hdrFontSize = isWide ? 10 : 9;
    let hdrLineH = hdrFontSize + 2;
    let maxLabelLines = cols.reduce((max, col, i) => {
      let maxChars = Math.max(
        4,
        Math.floor(colWidths[i] / (hdrFontSize * 0.62)),
      );
      return Math.max(max, wrapTextIntoLines(col.label, maxChars).length);
    }, 1);
    let COL_H = maxLabelLines * hdrLineH + COL_H_PAD;

    let totalH = headerH + COL_H + nRows * ROW_H;

    // Cumulative row Y offsets
    let rowYOffsets = [];
    let _accY = headerH + COL_H;
    rows.forEach(() => {
      rowYOffsets.push(_accY);
      _accY += ROW_H;
    });

    let el = this.element;
    if (!this.viewBox) {
      el.setAttribute('viewBox', `0 0 ${totalW} ${totalH}`);
    }
    el.setAttribute('width', '100%');
    el.removeAttribute('height');

    let svg = select(el);
    svg.selectAll('*').remove();

    // --- Defs: arrowhead markers ---
    let defs = svg.append('defs');
    defs
      .append('marker')
      .attr('id', 'pv-arrow-right')
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('refX', 5)
      .attr('refY', 3)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,0 L0,6 L6,3 z')
      .attr('fill', '#777');

    defs
      .append('marker')
      .attr('id', 'pv-arrow-left')
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('refX', 1)
      .attr('refY', 3)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M6,0 L6,6 L0,3 z')
      .attr('fill', '#777');

    // Clip column headers so text can never bleed into adjacent columns.
    // IDs are scoped to this element instance so multiple charts on the same
    // page don't interfere with each other.
    let pfx = this.elementId;
    cols.forEach((col, i) => {
      defs
        .append('clipPath')
        .attr('id', `${pfx}-ch-${i}`)
        .append('rect')
        .attr('x', colOffsets[i])
        .attr('y', 0)
        .attr('width', colWidths[i])
        .attr('height', COL_H);
    });

    let { headerFill, headerTextColor, containerBackground } = themeColors;

    // --- Container background ---
    svg
      .append('rect')
      .attr('width', totalW)
      .attr('height', totalH)
      .attr('fill', containerBackground || '#dce9e9');

    // --- Header bar (optional) ---
    if (this.showHeader) {
      let header = svg.append('g');
      header
        .append('rect')
        .attr('width', totalW)
        .attr('height', headerH)
        .attr('fill', headerFill || '#04667E');

      let textMidY = headerH / 2;

      // Student name (bold) + report label (lighter) — left side
      let leftText = header
        .append('text')
        .attr('x', 10)
        .attr('y', textMidY)
        .attr('dy', '0.35em')
        .attr('fill', headerTextColor || '#fff')
        .attr('font-family', 'sans-serif');

      leftText
        .append('tspan')
        .attr('font-size', 12)
        .attr('font-weight', '700')
        .text(this.headerName);

      leftText
        .append('tspan')
        .attr('dx', 8)
        .attr('font-size', 12)
        .attr('font-weight', 'normal')
        .attr('opacity', 0.65)
        .text(this.headerReport);
    }

    // --- Year label column background (white panel) ---
    svg
      .append('rect')
      .attr('x', 0)
      .attr('y', headerH)
      .attr('width', Y_W)
      .attr('height', totalH - headerH)
      .attr('fill', '#fafafa');

    // Vertical separator between year col and grid
    svg
      .append('line')
      .attr('x1', Y_W)
      .attr('y1', headerH)
      .attr('x2', Y_W)
      .attr('y2', totalH)
      .attr('stroke', 'rgba(0,0,0,0.12)')
      .attr('stroke-width', 1);

    // Left border of year column (inset by half stroke-width to stay inside viewport)
    svg
      .append('line')
      .attr('x1', 0.2)
      .attr('y1', headerH)
      .attr('x2', 0.2)
      .attr('y2', totalH)
      .attr('stroke', headerFill || '#04667E')
      .attr('stroke-opacity', 0.35)
      .attr('stroke-width', 0.4);

    // Bottom border spanning full table width (inset to stay inside viewport)
    svg
      .append('line')
      .attr('x1', 0)
      .attr('y1', totalH - 0.2)
      .attr('x2', totalW)
      .attr('y2', totalH - 0.2)
      .attr('stroke', headerFill || '#04667E')
      .attr('stroke-opacity', 0.35)
      .attr('stroke-width', 0.4);

    // "End of" label in corner
    svg
      .append('text')
      .attr('x', Y_W / 2)
      .attr('y', headerH + COL_H / 2)
      .attr('dy', '0.35em')
      .attr('fill', 'rgba(0,0,0,0.35)')
      .attr('font-size', 9)
      .attr('font-family', 'sans-serif')
      .attr('text-anchor', 'middle')
      .text('End of');

    // --- Column header row ---
    let colHeaderG = svg
      .append('g')
      .attr('transform', `translate(${Y_W},${headerH})`);

    cols.forEach((col, i) => {
      let x = colOffsets[i];
      let cw = colWidths[i];
      colHeaderG
        .append('rect')
        .attr('x', x)
        .attr('y', 0)
        .attr('width', cw)
        .attr('height', COL_H)
        .attr('fill', col.color || '#ccc');

      let midX = x + cw / 2;
      let maxCharsPerLine = Math.max(4, Math.floor(cw / (hdrFontSize * 0.62)));
      let lines = wrapTextIntoLines(col.label, maxCharsPerLine);
      let totalTextH = lines.length * hdrLineH;
      let startY = (COL_H - totalTextH) / 2 + hdrLineH * 0.85;

      let textEl = colHeaderG
        .append('text')
        .attr('fill', '#1a1a1a')
        .attr('font-size', hdrFontSize)
        .attr('font-weight', '700')
        .attr('font-family', 'sans-serif')
        .attr('text-anchor', 'middle')
        .attr('clip-path', `url(#${pfx}-ch-${i})`);

      lines.forEach((line, li) => {
        textEl
          .append('tspan')
          .attr('x', midX)
          .attr('y', startY + li * hdrLineH)
          .text(line);
      });
    });

    // Column header: vertical dividers + outline
    let borderStroke = headerFill || '#04667E';
    for (let i = 1; i < nCols; i++) {
      colHeaderG
        .append('line')
        .attr('x1', colOffsets[i])
        .attr('y1', 0)
        .attr('x2', colOffsets[i])
        .attr('y2', COL_H)
        .attr('stroke', borderStroke)
        .attr('stroke-opacity', 0.35)
        .attr('stroke-width', 0.4);
    }
    colHeaderG
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', totalW - Y_W)
      .attr('height', COL_H)
      .attr('fill', 'none')
      .attr('stroke', borderStroke)
      .attr('stroke-opacity', 0.35)
      .attr('stroke-width', 0.4);

    // --- Data rows ---
    rows.forEach((row, ri) => {
      let rowY = rowYOffsets[ri];
      let midY = ROW_H / 2;
      let rowG = svg.append('g').attr('transform', `translate(0,${rowY})`);

      // Horizontal row separator (full width, excluding year col top border)
      rowG
        .append('line')
        .attr('x1', Y_W)
        .attr('y1', 0)
        .attr('x2', totalW)
        .attr('y2', 0)
        .attr('stroke', 'rgba(0,0,0,0.08)')
        .attr('stroke-width', 0.5);

      // Also draw separator in year col
      rowG
        .append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', Y_W)
        .attr('y2', 0)
        .attr('stroke', 'rgba(0,0,0,0.08)')
        .attr('stroke-width', 0.5);

      // Year label — slightly smaller font on narrow layouts so it fits the
      // slimmer year column without risk of overflow.
      rowG
        .append('text')
        .attr('x', Y_W / 2)
        .attr('y', midY)
        .attr('dy', '0.35em')
        .attr('fill', '#111')
        .attr('font-size', isWide ? 10 : 9)
        .attr('font-weight', '700')
        .attr('font-family', 'sans-serif')
        .attr('text-anchor', 'middle')
        .text(row.label);

      // Cell backgrounds (grid columns only)
      row.cells.forEach((cell, ci) => {
        let cellX = Y_W + colOffsets[ci];
        rowG
          .append('rect')
          .attr('x', cellX)
          .attr('y', 0)
          .attr('width', colWidths[ci])
          .attr('height', ROW_H)
          .attr('fill', cols[ci] ? cols[ci].color : '#eee')
          .attr('fill-opacity', 0.5);
      });

      // Cross-cell right arrow (drawn as one line at row level)
      let rightSourceIdx = row.cells.findIndex((c) => c.hasArrowRight);
      let rightTargetIdx = row.cells.findIndex((c) => c.isArrowTarget);
      if (rightSourceIdx >= 0 && rightTargetIdx >= 0) {
        let srcW = colWidths[rightSourceIdx];
        let tgtW = colWidths[rightTargetIdx];
        let x1 =
          Y_W + colOffsets[rightSourceIdx] + srcW / 2 + (srcW - 16) / 2 + 4;
        let x2 =
          Y_W + colOffsets[rightTargetIdx] + tgtW / 2 - (tgtW - 16) / 2 - 4;
        rowG
          .append('line')
          .attr('x1', x1)
          .attr('y1', midY)
          .attr('x2', x2)
          .attr('y2', midY)
          .attr('stroke', '#777')
          .attr('stroke-width', 1)
          .attr('marker-end', 'url(#pv-arrow-right)');
      }

      // Cross-cell left arrow (regression)
      let leftSourceIdx = row.cells.findIndex((c) => c.hasArrowLeft);
      let leftTargetIdx = row.cells.findIndex((c) => c.isArrowTargetLeft);
      if (leftSourceIdx >= 0 && leftTargetIdx >= 0) {
        let srcW = colWidths[leftSourceIdx];
        let tgtW = colWidths[leftTargetIdx];
        let x1 =
          Y_W + colOffsets[leftSourceIdx] + srcW / 2 - (srcW - 16) / 2 - 4;
        let x2 =
          Y_W + colOffsets[leftTargetIdx] + tgtW / 2 + (tgtW - 16) / 2 + 4;
        rowG
          .append('line')
          .attr('x1', x1)
          .attr('y1', midY)
          .attr('x2', x2)
          .attr('y2', midY)
          .attr('stroke', '#777')
          .attr('stroke-width', 1)
          .attr('marker-end', 'url(#pv-arrow-right)');
      }

      // Badges (drawn on top of arrows)
      row.cells.forEach((cell, ci) => {
        let cellX = Y_W + colOffsets[ci];
        let cw = colWidths[ci];
        let badges = cell.badges || [];

        if (badges.length === 2) {
          // Two chips in same column — side by side (smaller to keep arrow visible).
          // For regression (left), swap order so newer (lower) chip is on the left
          // and older (higher) chip is on the right, matching arrow direction.
          let isLeft = cell.sameCellDirection === 'left';
          let leftBadge = isLeft ? badges[1] : badges[0];
          let rightBadge = isLeft ? badges[0] : badges[1];

          let pairW = (cw - PAIR_GAP - 8) / 2;
          let startX = cellX + (cw - pairW * 2 - PAIR_GAP) / 2;
          let b1X = startX + pairW + PAIR_GAP;

          drawBadge(
            rowG,
            startX,
            midY - PAIR_BADGE_H / 2,
            pairW,
            PAIR_BADGE_H,
            PAIR_BADGE_R,
            leftBadge,
            headerFill,
            PAIR_BADGE_FONT,
          );

          if (cell.sameCellDirection) {
            let arrowX1 = startX + pairW + 2;
            let arrowX2 = b1X - 2;
            if (cell.sameCellDirection === 'right') {
              rowG
                .append('line')
                .attr('x1', arrowX1)
                .attr('y1', midY)
                .attr('x2', arrowX2)
                .attr('y2', midY)
                .attr('stroke', '#777')
                .attr('stroke-width', 1)
                .attr('marker-end', 'url(#pv-arrow-right)');
            } else {
              rowG
                .append('line')
                .attr('x1', arrowX2)
                .attr('y1', midY)
                .attr('x2', arrowX1)
                .attr('y2', midY)
                .attr('stroke', '#777')
                .attr('stroke-width', 1)
                .attr('marker-end', 'url(#pv-arrow-right)');
            }
          }

          drawBadge(
            rowG,
            b1X,
            midY - PAIR_BADGE_H / 2,
            pairW,
            PAIR_BADGE_H,
            PAIR_BADGE_R,
            rightBadge,
            headerFill,
            PAIR_BADGE_FONT,
          );
        } else if (badges.length === 1) {
          let badgeW = cw - 16;
          let badgeX = cellX + (cw - badgeW) / 2;
          drawBadge(
            rowG,
            badgeX,
            midY - BADGE_H / 2,
            badgeW,
            BADGE_H,
            BADGE_R,
            badges[0],
            headerFill,
            // Narrow columns get a slightly smaller font so "MMM YY" labels
            // (6 chars) fit comfortably without relying on tight font metrics.
            isWide ? BADGE_FONT : BADGE_FONT - 1,
          );
        }
      });

      // Row: vertical column dividers + outline over grid area
      for (let i = 1; i < nCols; i++) {
        rowG
          .append('line')
          .attr('x1', Y_W + colOffsets[i])
          .attr('y1', 0)
          .attr('x2', Y_W + colOffsets[i])
          .attr('y2', ROW_H)
          .attr('stroke', headerFill || '#04667E')
          .attr('stroke-opacity', 0.35)
          .attr('stroke-width', 0.4);
      }
      rowG
        .append('rect')
        .attr('x', Y_W)
        .attr('y', 0)
        .attr('width', totalW - Y_W)
        .attr('height', ROW_H)
        .attr('fill', 'none')
        .attr('stroke', headerFill || '#04667E')
        .attr('stroke-opacity', 0.35)
        .attr('stroke-width', 0.4);
    });
  }
}

function wrapTextIntoLines(label, maxCharsPerLine) {
  let words = label.split(' ');
  let lines = [];
  let current = '';
  words.forEach((word) => {
    let test = current ? `${current} ${word}` : word;
    if (test.length > maxCharsPerLine && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  });
  if (current) {
    lines.push(current);
  }
  return lines;
}

function drawBadge(g, x, y, w, h, r, badge, primaryColor, fontSize) {
  let isPrimary = badge.type === 'primary';
  g.append('rect')
    .attr('x', x)
    .attr('y', y)
    .attr('width', w)
    .attr('height', h)
    .attr('rx', r)
    .attr('ry', r)
    .attr('fill', isPrimary ? primaryColor || '#04667E' : '#fff')
    .attr('stroke', isPrimary ? 'none' : 'rgba(0,0,0,0.2)')
    .attr('stroke-width', 0.75);

  let effectiveFontSize = fontSize || BADGE_FONT;
  let textEl = g
    .append('text')
    .attr('x', x + w / 2)
    .attr('y', y + h / 2)
    .attr('dy', '0.35em')
    .attr('fill', isPrimary ? '#fff' : '#333')
    .attr('font-size', effectiveFontSize)
    .attr('font-weight', '600')
    .attr('font-family', 'sans-serif')
    .attr('text-anchor', 'middle')
    .text(badge.label);

  // Guard against font-metric differences across devices: if the text is
  // estimated to exceed the badge interior, compress it to fit. The 0.65
  // coefficient is conservative enough to catch wide Android fonts while
  // leaving short labels (e.g. "Ok") completely unconstrained.
  let maxTextW = w - 4;
  if (badge.label.length * effectiveFontSize * 0.65 > maxTextW) {
    textEl
      .attr('textLength', Math.max(maxTextW, 1))
      .attr('lengthAdjust', 'spacingAndGlyphs');
  }
}
